import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ParticipantAvatar, useRecordVisit } from '@/design';
import { useAuth, useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import { accountsApi } from '../api/accounts-api';
import type { IAdminAccountDetail } from '../api/account-contracts';
import type { AccountRelation } from '../api/participant-contracts';
import {
  AccountKindChip,
  AccountRoleChips,
  AccountStateChip,
  ActivityFactItems,
  AddressFact,
  absent,
  relationWords
} from '../components/account-facts';
import {
  AccountRolesCard,
  groupCatalogue,
  usePermissionCatalogue,
  useUsersAndRolesTabs
} from '../components/account-roles';
import {
  AccountStatusNotice,
  AccountStatusPress,
  type IAccountStatusSaid
} from '../components/account-status';
import {
  AdminPage,
  Fact,
  LoadError,
  TableSkeleton,
  useAdminData
} from '../components/admin-page';
import { LoginAddressCard, NO_LINK_WHILE_DISABLED } from '../components/login-address-card';
import {
  BackToList,
  type ICardLinkState,
  ParticipantLink,
  searchFrom
} from '../components/participant-link';
import { SignInCard, accountPowers } from '../components/sign-in-card';
import { TEAM_PATHS, userPath } from '../paths';

/**
 * One account: what survey-api holds about the seat — its kind, its Team Portal roles and what
 * they let it do (SR-228), how it signs in (SR-476) — and what the viewer's permissions let it
 * change: the roles, the status, the invitation to sign in, the identity's link, the address.
 *
 * ## Why this page is addressed by email
 *
 * Because staff come to it holding an address — the list's rows, the hub's accounts, a search.
 * Every write names its target by id instead, which the detail carries (`accountId`), so the
 * address stays the page's key.
 *
 * Until SR-210 the address was not just the key but the whole lookup: the only read was the list,
 * and this page found its row through that list's `?email=` filter — a **substring** match,
 * so `a@b.com` matched `xa@b.com` too, over a page ordered `createdAt DESC` that could not
 * say which row was meant. The needle was picked out of a `PAGE_SIZE_MAX` page and compared
 * case-insensitively by hand.
 *
 * `GET /api/v1/admin/accounts/by-email/{email}` replaced all of that with one exact lookup —
 * case-insensitive in the server, `account.email` being `citext` — that answers this account
 * or a 404. survey-api ships an id lookup beside it, which this page does not need for the
 * reason above. What the detail carries over the list row is the rest of this file: `activity`,
 * `participants` with ids, `emailVerifiedAt` and — since SR-476 — `idp`.
 *
 * ## What it offers, and what it still cannot
 *
 * **How it signs in** is the Sign-in card (`components/sign-in-card.tsx`, SR-476), right under
 * the account's facts — in a component of its own since the person hub draws it too, for a
 * viewer holding `accounts.browse`: the identity the account signs in with at Invent's IdP, its
 * invitations, and "Send invitation" and "Unlink". An invitation causes a mail, so it draws the
 * test inbox (`TestInboxField`) where the deployment holds its mail to an allowlist: dev and UAT.
 *
 * Participant link editing arrived with survey-api's block 6, and it is not here: the link
 * and its undo are addressed by the **participant** (`…/participants/{id}/account-links`), and
 * pressed on the person hub, which is where the question they answer — who can sign in as this
 * person — is asked. The participants card below leads there.
 *
 * SR-416 added the address (`components/login-address-card.tsx`, after the Sign-in card): staff
 * move an advisor's mail to a new address, every session ending with it. The page
 * then **follows the account to its new address** — it is addressed by email, so the old address
 * would now answer "No such account" — and says there what changed (`IUserCardState`).
 *
 * SR-421 added the status (`components/account-status.tsx`, over survey-api's SR-442): "Disable
 * account…" / "Activate account…" in the header, beside the title — it acts on the whole
 * account, not on one of the cards below — and a disabled account said at the top in red, with a
 * "disabled" chip under the title, since nothing else on the page lets anybody in.
 *
 * One remains absent, and is left out rather than rendered as a control that would lie:
 *
 * - **The `account_event` history.** Every account write records to it, and SR-210 reads one
 *   instant back out of it into `activity`. The feed is read over HTTP by participant alone
 *   (SR-215's activity route, the person hub's Activity), so no write here leaves a visible
 *   trail on this page; on the hub, a write's row shows below it.
 */
/**
 * The user card's history state: the list's search it was opened from, and — on the card a
 * login address change navigated to — the address it left, said once at the top.
 */
interface IUserCardState extends Partial<ICardLinkState> {
  emailChangedFrom?: string;
}

function emailChangedFrom(state: unknown): Nullable<string> {
  const from = (state as Nullable<IUserCardState>)?.emailChangedFrom;
  return typeof from === 'string' ? from : null;
}

export function AdminUserDetailPage() {
  // Taken as React Router hands it over, and **not** decoded again. It decodes params itself,
  // per segment, so a second `decodeURIComponent` here was a no-op for most addresses and
  // wrong for two kinds: it threw `URIError` on a literal `%` in a local part — a white
  // screen, this app having no error boundary — and it turned a literal `%2F` into a slash,
  // mis-addressing the exact lookup that the catch-all route exists to serve.
  const { email = '' } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { user: signedIn } = useAuth();
  const can = useCan();

  // `[email]`: this element is not remounted when the route parameter changes, so without it
  // a second account opened from the list would render the first one's data.
  //
  // Wrapped in an object rather than returned bare, because `useAdminData` uses `null` for
  // "not loaded yet" and a bare `IAdminAccountDetail | null` would make that indistinguishable
  // from "no such address" — the screen would sit on a skeleton forever for a typo.
  const { data, error, reload } = useAdminData<{ account: Nullable<IAdminAccountDetail> }>(
    async (signal) => ({ account: await accountsApi.getAccountByEmail(email, signal) }),
    [email]
  );

  const account = data?.account ?? null;
  useRecordVisit(
    account ? { kind: 'account', path: userPath(account.email), label: account.email } : null
  );

  // Shared by the page's writes, derived as the person hub derives them for its accounts.
  const powers = accountPowers(can, signedIn?.email, account);
  const { isSelf, canManage, canGrant } = powers;
  const tabs = useUsersAndRolesTabs();
  const changedFrom = emailChangedFrom(state);
  const [statusSaid, setStatusSaid] = React.useState<Nullable<IAccountStatusSaid>>(null);

  return (
    <AdminPage
      title={account?.email ?? email}
      crumb="Users & roles"
      tabs={tabs}
      meta={
        account?.status === 'disabled' ? (
          <span data-account-status="disabled">
            <AccountStateChip state="disabled" />
          </span>
        ) : undefined
      }
      actions={
        account && (
          <AccountStatusPress
            account={account}
            isSelf={isSelf}
            canManage={canManage}
            canGrant={canGrant}
            onChanged={reload}
            onSaid={setStatusSaid}
          />
        )
      }
    >
      <BackToList list={TEAM_PATHS.users} search={searchFrom(state)} all="All users" />

      {account && <AccountStatusNotice account={account} said={statusSaid} />}

      {/* Whatever the read at the new address did: the change landed, and a read that failed
          or found nobody must not leave the page silent about it. */}
      {changedFrom && (
        <Alert role="status" data-email-changed>
          <AlertTitle>Email address changed from {changedFrom}</AlertTitle>
          <AlertDescription>
            Every session of the account ended.{' '}
            {account?.status === 'disabled'
              ? NO_LINK_WHILE_DISABLED
              : 'The new address is unconfirmed until its owner opens the link mailed to it.'}
          </AlertDescription>
        </Alert>
      )}

      {error &&
        (data === null ? (
          <LoadError message={error} />
        ) : (
          // A later read that failed over a card already drawn — after a write, most often —
          // says so and keeps the account as last read, never the first load's banner.
          <Alert variant="destructive">
            <AlertTitle>Could not refresh — showing the account as last read</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ))}

      {/* Three arms, and the first is what keeps the third honest. `useAdminData` leaves
          `data` null when a read throws, so a refusal that is not a 404 must not fall through
          to "no such account": `apiGetOrNull` turns 404 into a loaded-but-empty result and
          rethrows everything else, which means a 403 — the policy refusing a session whose
          roles no longer include `accounts.browse` — reaches here with nothing learned about
          whether the account exists. Stating that it does not would be the most confident thing
          on the page and the only false one. */}
      {data === null ? (
        error ? null : <TableSkeleton rows={4} />
      ) : account ? (
        <>
          <AccountFacts account={account} />
          {/* How it signs in, right under what it is: the first question staff bring here.
              Keyed on the account: the page is not remounted when the list beside it opens
              another, and one account's note or inbox draft must not show on the next. It
              reports inside itself, beside its controls, and re-reads the page on a write. */}
          <SignInCard
            key={account.accountId}
            account={account}
            powers={powers}
            onReread={() => void reload()}
          />
          {/* The SR-210 cards, above the roles' writes deliberately: each one is context for a
              control below it — who loses access with the seat, and what it can do. */}
          <ActivityFacts account={account} />
          <ParticipantFacts account={account} />
          <PermissionFacts account={account} catalogueReadable={canGrant} />
          {/* SR-228's roles write, saying its refusal beside it and re-reading on a landing,
              told whether the re-read landed. The kind's card that stood under it is gone
              (SR-411): an account's portal is fixed when it is made. */}
          <AccountRolesCard
            account={account}
            isSelf={isSelf}
            canGrant={canGrant}
            onChanged={reload}
          />
          {/* Keyed on the address: the element is not remounted when the page follows the
              account to its new one, and a draft typed for the old card must not survive. */}
          <LoginAddressCard
            key={account.email}
            account={account}
            canManage={canManage}
            onChanged={({ from, to }) => {
              const next: IUserCardState = {
                ...(state as Nullable<IUserCardState>),
                emailChangedFrom: from
              };
              navigate(userPath(to), { replace: true, state: next });
            }}
            onReread={() => void reload()}
          />
        </>
      ) : (
        <Alert>
          <AlertTitle>No such account</AlertTitle>
          <AlertDescription>
            Nothing on this server is registered at <strong>{email}</strong>.
          </AlertDescription>
        </Alert>
      )}
    </AdminPage>
  );
}

/** Said by both places that render an account's participants — the facts row and the card —
 *  which describe the same emptiness and must not come to word it differently. */
const NO_PARTICIPANTS = 'Nothing — this account can sign in but has no survey to fill.';

function AccountFacts({ account }: { account: IAdminAccountDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Account</CardTitle>
        <CardDescription>What the system holds about this seat.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
        <Fact label="Email" value={account.email} />
        <Fact label="Account id" value={account.accountId} />
        <Fact label="Kind" value={<AccountKindChip kind={account.kind} />} />
        <Fact
          label="Roles"
          value={
            account.kind === 'staff' ? (
              <AccountRoleChips kind={account.kind} roles={account.roles} />
            ) : (
              absent('None — roles are a Team Portal account’s')
            )
          }
        />
        <Fact
          label="Status"
          value={
            <Badge variant={account.status === 'active' ? 'outline' : 'destructive'}>
              {account.status}
            </Badge>
          }
        />
        <Fact label="Registered" value={formatDateTime(account.createdAt)} />
        <Fact label="Last changed" value={formatDateTime(account.updatedAt)} />
        <AddressFact emailVerifiedAt={account.emailVerifiedAt} />
        <div className="sm:col-span-2">
          <Fact
            label="Speaks for"
            value={
              account.participantNames.length === 0
                ? absent(NO_PARTICIPANTS)
                : account.participantNames.join(', ')
            }
          />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Three numbers with two lifetimes, and the card says which is which — because read as one
 * block they make a working account look abandoned. `ActivityFactItems` says why each one
 * lives as long as it does.
 */
function ActivityFacts({ account }: { account: IAdminAccountDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity</CardTitle>
        <CardDescription>
          The last sign-in survives; the session facts beside it are purged and go quiet on
          their own.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
        <ActivityFactItems activity={account.activity} />
      </CardContent>
    </Card>
  );
}

/**
 * The subjects this account acts for, with the ids the list row never carried.
 *
 * `GET /my/participants`' shape, which is why the kind is spelled with the survey domain's
 * own `ParticipantKind` — one server enum, one vocabulary on this side.
 *
 * Read-only here: the link and its undo are the person hub's (survey-api's block 6 addresses
 * them by participant), so an id here is something to quote in a ticket, and each name leads to
 * the hub where the link is changed. `relation` is said only when it is not `self`: "as point of
 * contact" for a `contact` link (SR-416 — an accepted invitation's, the account acting for the
 * participant without being theirs), any other spelling as sent.
 */
function ParticipantFacts({ account }: { account: IAdminAccountDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Participants</CardTitle>
        <CardDescription>
          What this account can open a survey as. A link is made or undone on their person hub,
          under Account & access, which each name opens.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm">
        {account.participants.length === 0 ? (
          <p>{absent(NO_PARTICIPANTS)}</p>
        ) : (
          account.participants.map((participant) => (
            <div key={participant.participantId} className="flex items-center gap-3">
              <ParticipantAvatar
                photoUri={participant.photoUri}
                displayName={participant.displayName}
                decorative
              />
              <div className="grid min-w-0 gap-0.5">
                <span className="flex flex-wrap items-center gap-2">
                  <ParticipantLink participant={participant} />
                  <Badge variant="soft">{participant.kind}</Badge>
                  <RelationNote relation={participant.relation} />
                </span>
                <span className="text-xs break-all text-muted-foreground">
                  {participant.participantId}
                </span>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

/** "as point of contact" beside a participant the account is not the participant's own for. */
function RelationNote({ relation }: { relation: AccountRelation }) {
  const words = relationWords(relation);
  return words === null ? null : (
    <span className="text-muted-foreground">as {words.toLowerCase()}</span>
  );
}

/**
 * "What this account can do" — the detail's `permissions[]`, survey-api's effective set, drawn
 * and never compared with the reader's own (whether one outranks the other is survey-api's D5).
 * Grouped by `GET /admin/permissions` where the reader may read it (`roles.manage`), in the
 * catalogue's order and words; else the codes as sent. A code the catalogue lacks is listed after
 * its groups, by its code — nothing dropped.
 */
function PermissionFacts({
  account,
  catalogueReadable
}: {
  account: IAdminAccountDetail;
  catalogueReadable: boolean;
}) {
  const { data, error } = usePermissionCatalogue(catalogueReadable);
  const catalogue = data?.permissions ?? null;
  const held: readonly string[] = account.permissions;

  let body: React.ReactNode;
  if (held.length === 0) {
    body = absent(
      account.kind === 'staff'
        ? 'Nothing yet — no Team Portal role gives this account a permission.'
        : 'Nothing in the Team Portal — an Advisor Portal account fills surveys.'
    );
  } else if (catalogue === null) {
    body = (
      <>
        {catalogueReadable && error && (
          <p className="text-muted-foreground">
            The permission catalogue could not be read ({error}), so each is named by its code.
          </p>
        )}
        <PermissionCodes codes={held} />
      </>
    );
  } else {
    const groups = groupCatalogue(catalogue, (code) => held.includes(code));
    const unknown = held.filter((code) => !catalogue.some((entry) => entry.code === code));
    body = (
      <>
        {groups.map((group) => (
          <div key={group.group} className="grid gap-1" data-permission-group={group.group}>
            <span className="text-xs tracking-wide text-muted-foreground uppercase">
              {group.label}
            </span>
            <ul className="grid gap-1">
              {group.entries.map((entry) => (
                <li key={entry.code} data-permission={entry.code}>
                  {entry.description}{' '}
                  <code className="text-xs text-muted-foreground">{entry.code}</code>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {unknown.length > 0 && (
          <div className="grid gap-1">
            <span className="text-xs tracking-wide text-muted-foreground uppercase">
              Not in the catalogue
            </span>
            <PermissionCodes codes={unknown} />
          </div>
        )}
      </>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>What this account can do</CardTitle>
        <CardDescription>
          The permissions its Team Portal roles give it together, as the system resolves them.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm">{body}</CardContent>
    </Card>
  );
}

function PermissionCodes({ codes }: { codes: readonly string[] }) {
  return (
    <ul className="flex flex-wrap gap-x-3 gap-y-1">
      {codes.map((code) => (
        <li key={code} data-permission={code}>
          <code className="text-xs">{code}</code>
        </li>
      ))}
    </ul>
  );
}
